import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  query,
  orderBy,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { fetchHRTemplates } from '../utils/hrTemplates';
import { fetchTemplateStructure } from './hrStructureService';

const TEMPLATES_COLLECTION = 'templatesCollections';

/**
 * Next version in the x.y scheme: 1.0 → 1.1 → … → 1.9 → 2.0 → 2.1.
 * First upload (no current version) is 1.0; unparseable versions restart at 1.0.
 */
export const suggestNextVersion = (current) => {
  const match = String(current || '').trim().match(/^v?(\d+)(?:\.(\d+))?/);
  if (!match) return '1.0';
  const major = parseInt(match[1], 10);
  const minor = match[2] === undefined ? 0 : parseInt(match[2], 10);
  return minor >= 9 ? `${major + 1}.0` : `${major}.${minor + 1}`;
};

export const getFileType = (fileName = '') => {
  const extension = fileName.split('.').pop().toLowerCase();
  if (['xlsx', 'xls'].includes(extension)) return 'XLS';
  if (['pptx', 'ppt'].includes(extension)) return 'PPT';
  if (extension === 'pdf') return 'PDF';
  return 'DOC';
};

/**
 * Returns a department's editable structure (sections from
 * templatesStructures/<structureId>) with each item augmented with its matching
 * Firestore template doc (the "latest" pointer).
 * Shape: [{ id, label, items, entries: [{ title, name, description, doc }] }]
 *
 * @param {{ pillar?: string, structureId?: string, defaultSections?: Array }} opts
 */
export const fetchTemplatesGrouped = async ({ pillar = 'HR', structureId = 'hr', defaultSections = [] } = {}) => {
  const [sections, documents] = await Promise.all([
    fetchTemplateStructure(structureId, defaultSections),
    fetchHRTemplates(),
  ]);
  // Key by stable catalogKey when present, otherwise fall back to title so the
  // card stays linked even if staff edit the document's display title. Only
  // docs from this pillar are matched (legacy docs without a pillar are HR),
  // so same-named templates in other departments never cross-link.
  const byKey = new Map();
  documents.forEach((d) => {
    if ((d.pillar || 'HR') !== pillar) return;
    const key = d.catalogKey || d.title;
    const existing = byKey.get(key);
    if (!existing || (d.updatedAt || '') > (existing.updatedAt || '')) {
      byKey.set(key, d);
    }
  });

  return sections.map((section) => ({
    ...section,
    entries: section.items.map((entry) => ({
      ...entry,
      // Fallback to entry.name self-heals docs orphaned by an old title rename
      // (legacy docs without catalogKey lost their link when title changed).
      doc: byKey.get(entry.title) || (entry.name && byKey.get(entry.name)) || null,
    })),
  }));
};

const versionsRef = (docId) => collection(db, TEMPLATES_COLLECTION, docId, 'versions');

/**
 * Lazily seed an initial version entry from the parent's flat fields when a
 * legacy doc (created before version history existed) has no versions yet.
 */
const seedInitialVersionIfNeeded = async (docId, parentData) => {
  const snap = await getDocs(versionsRef(docId));
  if (!snap.empty) return;
  if (!parentData?.fileUrl) return;
  await addDoc(versionsRef(docId), {
    version: parentData.version || '1.0',
    fileName: parentData.fileName || '',
    fileSize: parentData.fileSize || 0,
    fileUrl: parentData.fileUrl,
    storagePath: parentData.storagePath || '',
    type: parentData.type || getFileType(parentData.fileName || ''),
    uploadedByUid: parentData.uploadedByUid || '',
    uploadedByName: parentData.uploadedByName || 'Unknown',
    uploadedAt: parentData.updatedAt || parentData.createdAt || new Date().toISOString(),
    notes: 'Imported from prior upload',
  });
};

/**
 * Upload a new file as a new version of a template. Creates the parent doc if it
 * doesn't exist yet, appends an immutable version entry, and updates the parent
 * "latest" pointer (fileUrl/version/etc.) for backward-compatible readers.
 *
 * @param {{ docId?: string, title: string, catalogKey?: string, pillar?: string, file: File, version: string, notes?: string, user?: { uid?: string, name?: string } }} opts
 */
export const uploadNewVersion = async ({ docId, title, catalogKey, pillar = 'HR', file, version, notes = '', user = {} }) => {
  if (!file) throw new Error('A file is required');
  if (!version || !version.trim()) throw new Error('A version number is required');

  const timestamp = Date.now();
  const fileName = `${timestamp}_${file.name}`;
  const storageRef = ref(storage, `${TEMPLATES_COLLECTION}/${pillar}/${fileName}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  if (!downloadURL) throw new Error('Failed to get download URL from Storage');

  const nowIso = new Date().toISOString();
  const type = getFileType(file.name);

  const latestPointer = {
    title,
    pillar,
    type,
    fileName: file.name,
    fileSize: file.size,
    fileUrl: downloadURL,
    version: version.trim(),
    storagePath: storageRef.fullPath,
    originalFileName: file.name,
    unlocked: true,
    updatedAt: nowIso,
  };

  let targetId = docId;
  if (!targetId) {
    const created = await addDoc(collection(db, TEMPLATES_COLLECTION), {
      ...latestPointer,
      catalogKey: catalogKey || title,
      description: '',
      createdAt: nowIso,
    });
    targetId = created.id;
  } else {
    const parentSnap = await getDoc(doc(db, TEMPLATES_COLLECTION, targetId));
    if (parentSnap.exists()) {
      await seedInitialVersionIfNeeded(targetId, parentSnap.data());
    }
    await updateDoc(doc(db, TEMPLATES_COLLECTION, targetId), latestPointer);
  }

  const versionEntry = await addDoc(versionsRef(targetId), {
    version: version.trim(),
    fileName: file.name,
    fileSize: file.size,
    fileUrl: downloadURL,
    storagePath: storageRef.fullPath,
    type,
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: nowIso,
    notes: notes.trim(),
  });

  await updateDoc(doc(db, TEMPLATES_COLLECTION, targetId), { currentVersionId: versionEntry.id });

  return { docId: targetId, versionId: versionEntry.id };
};

/**
 * Save the tailorable HTML (with {{tokens}}) on a template. Creates the parent
 * doc if the template slot has no file uploaded yet, so HTML-only templates
 * still work. Pass an empty string to clear the tailoring HTML.
 *
 * @param {{ docId?: string, title: string, catalogKey?: string, pillar?: string, html: string, tokens?: string[], user?: { uid?: string, name?: string } }} opts
 */
export const saveTailorHtml = async ({ docId, title, catalogKey, pillar = 'HR', html, tokens = [], user = {} }) => {
  const nowIso = new Date().toISOString();
  const fields = {
    tailorHtml: html || '',
    tailorTokens: tokens,
    tailorHtmlUpdatedAt: nowIso,
    tailorHtmlUpdatedByUid: user.uid || '',
    tailorHtmlUpdatedByName: user.name || 'Unknown',
    updatedAt: nowIso,
  };

  if (docId) {
    await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), fields);
    return { docId };
  }

  const created = await addDoc(collection(db, TEMPLATES_COLLECTION), {
    title,
    pillar,
    catalogKey: catalogKey || title,
    description: '',
    unlocked: true,
    createdAt: nowIso,
    ...fields,
  });
  return { docId: created.id };
};

/**
 * Save which tailored-download formats are enabled for a template (shown as
 * download buttons in the portal Tailor modal). Creates the parent doc if the
 * slot has no doc yet, mirroring saveTailorHtml.
 *
 * @param {{ docId?: string, title: string, catalogKey?: string, pillar?: string, formats: string[], user?: { uid?: string, name?: string } }} opts
 */
export const saveTailorFormats = async ({ docId, title, catalogKey, pillar = 'HR', formats, user = {} }) => {
  const nowIso = new Date().toISOString();
  const fields = {
    tailorFormats: formats,
    updatedAt: nowIso,
  };

  if (docId) {
    await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), fields);
    return { docId };
  }

  const created = await addDoc(collection(db, TEMPLATES_COLLECTION), {
    title,
    pillar,
    catalogKey: catalogKey || title,
    description: '',
    unlocked: true,
    createdAt: nowIso,
    ...fields,
  });
  return { docId: created.id };
};

/**
 * Save a template's Canva share link. Creates the parent doc if the slot has
 * no doc yet, mirroring saveTailorHtml. Pass an empty string to clear the link.
 *
 * @param {{ docId?: string, title: string, catalogKey?: string, pillar?: string, url: string, user?: { uid?: string, name?: string } }} opts
 */
export const saveCanvaUrl = async ({ docId, title, catalogKey, pillar = 'HR', url, user = {} }) => {
  const nowIso = new Date().toISOString();
  const fields = {
    canvaUrl: url || '',
    canvaUrlUpdatedAt: nowIso,
    canvaUrlUpdatedByUid: user.uid || '',
    canvaUrlUpdatedByName: user.name || 'Unknown',
    updatedAt: nowIso,
  };

  if (docId) {
    await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), fields);
    return { docId };
  }

  const created = await addDoc(collection(db, TEMPLATES_COLLECTION), {
    title,
    pillar,
    catalogKey: catalogKey || title,
    description: '',
    unlocked: true,
    createdAt: nowIso,
    ...fields,
  });
  return { docId: created.id };
};

/**
 * Update editable metadata on a template's parent doc. Always pass catalogKey
 * (the stable structure-item title) — it backfills legacy docs so renaming the
 * display title can never unlink the doc from its card.
 */
export const updateTemplateDetails = async (docId, { title, description, catalogKey }) => {
  if (!docId) throw new Error('docId is required');
  const updates = { updatedAt: new Date().toISOString() };
  if (typeof title === 'string') updates.title = title;
  if (typeof description === 'string') updates.description = description;
  if (typeof catalogKey === 'string' && catalogKey) updates.catalogKey = catalogKey;
  await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), updates);
};

/** List a template's version history, newest first. */
export const fetchVersionHistory = async (docId) => {
  if (!docId) return [];
  const parentSnap = await getDoc(doc(db, TEMPLATES_COLLECTION, docId));
  if (parentSnap.exists()) {
    await seedInitialVersionIfNeeded(docId, parentSnap.data());
  }
  const q = query(versionsRef(docId), orderBy('uploadedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Roll the parent "latest" pointer back to a prior version's file, and append a
 * new version entry recording the rollback (history is never destroyed).
 *
 * @param {string} docId
 * @param {string} versionId
 * @param {{ uid?: string, name?: string }} [user]
 */
export const rollbackToVersion = async (docId, versionId, user = {}) => {
  if (!docId || !versionId) throw new Error('docId and versionId are required');
  const versionSnap = await getDoc(doc(db, TEMPLATES_COLLECTION, docId, 'versions', versionId));
  if (!versionSnap.exists()) throw new Error('Version not found');
  const v = versionSnap.data();
  const nowIso = new Date().toISOString();

  await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), {
    type: v.type,
    fileName: v.fileName,
    fileSize: v.fileSize,
    fileUrl: v.fileUrl,
    version: v.version,
    storagePath: v.storagePath,
    updatedAt: nowIso,
  });

  const versionEntry = await addDoc(versionsRef(docId), {
    version: v.version,
    fileName: v.fileName,
    fileSize: v.fileSize,
    fileUrl: v.fileUrl,
    storagePath: v.storagePath,
    type: v.type,
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: nowIso,
    notes: `Rolled back to v${v.version}`,
  });

  await updateDoc(doc(db, TEMPLATES_COLLECTION, docId), { currentVersionId: versionEntry.id });
  return { versionId: versionEntry.id };
};
